import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "app/store";
import { FEATURE_FLAGS } from "constants/api";
import http from "utils/http";

interface FeatureFlagResponse<T> extends ApiResponse {
  data: T;
}

interface UpdateFeatureFlagArgs extends ReduxAction {
  key: string;
  payload: { enabled: boolean };
}

type ThunkConfig = { state: RootState; rejectValue: string };

const errorMessage = (error: unknown, fallback: string) =>
  (error as { message?: string } | null)?.message || fallback;

export const fetchFeatureFlags = createAsyncThunk<
  IFeatureFlag[], ReduxAction, ThunkConfig
>(
  FEATURE_FLAGS.LIST,
  async ({ onFailure }, { signal, rejectWithValue }) => {
    try {
      const response = await http.get<unknown, FeatureFlagResponse<IFeatureFlag[]>>(
        FEATURE_FLAGS.LIST,
        { signal }
      );
      if (!response.status) {
        throw new Error(response.message || "Could not load feature flags.");
      }
      return response.data;
    } catch (error) {
      if (!signal.aborted) onFailure?.(error);
      return rejectWithValue(errorMessage(error, "Could not load feature flags."));
    }
  },
  {
    condition: (_, { getState }) => getState().featureFlags.savingKey === null,
  }
);

export const updateFeatureFlag = createAsyncThunk<
  IFeatureFlag, UpdateFeatureFlagArgs, ThunkConfig
>(
  FEATURE_FLAGS.DETAIL,
  async ({ key, payload, onSuccess, onFailure }, { rejectWithValue }) => {
    try {
      const response = await http.patch<unknown, FeatureFlagResponse<IFeatureFlag>>(
        FEATURE_FLAGS.DETAIL.replace(":key", encodeURIComponent(key)),
        payload
      );
      if (!response.status) {
        throw new Error(response.message || "Could not update the feature flag.");
      }
      onSuccess?.(response.data);
      return response.data;
    } catch (error) {
      const message = errorMessage(error, "Could not update the feature flag.");
      onFailure?.({ message });
      return rejectWithValue(message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { isLoading, error, savingKey } = getState().featureFlags;
      return !isLoading && !error && savingKey === null;
    },
  }
);
